import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import api from '../services/authService';
import { colors } from '../styles/colors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface Stats {
  totalCandidates: number;
  totalCompanies: number;
  recentCandidates: number;
  recentCompanies: number;
}

interface ChartData {
  candidates: Array<{ month: string; count: number }>;
  companies: Array<{ month: string; count: number }>;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats>({
    totalCandidates: 0,
    totalCompanies: 0,
    recentCandidates: 0,
    recentCompanies: 0,
  });
  const [chartData, setChartData] = useState<ChartData>({ candidates: [], companies: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchChartData();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await api.get('/dashboard/charts');
      setChartData(response.data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={styles.loading}>Carregando...</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Dashboard</h1>
      
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Total de Candidatos</h3>
          <p style={styles.statNumber}>{stats.totalCandidates.toLocaleString()}</p>
        </div>
        
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Total de Empresas</h3>
          <p style={styles.statNumber}>{stats.totalCompanies.toLocaleString()}</p>
        </div>
        
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Candidatos Recentes (30 dias)</h3>
          <p style={styles.statNumber}>{stats.recentCandidates.toLocaleString()}</p>
        </div>
        
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Empresas Recentes (30 dias)</h3>
          <p style={styles.statNumber}>{stats.recentCompanies.toLocaleString()}</p>
        </div>
      </div>
      
      <div style={styles.chartsContainer}>
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Novos Candidatos por Mês</h3>
          <Bar
            data={{
              labels: chartData.candidates.map(item => {
                const [year, month] = item.month.split('-');
                return `${month}/${year}`;
              }),
              datasets: [
                {
                  label: 'Candidatos',
                  data: chartData.candidates.map(item => item.count),
                  backgroundColor: colors.primary,
                  borderColor: colors.primary,
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </div>
        
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Novas Empresas por Mês</h3>
          <Bar
            data={{
              labels: chartData.companies.map(item => {
                const [year, month] = item.month.split('-');
                return `${month}/${year}`;
              }),
              datasets: [
                {
                  label: 'Empresas',
                  data: chartData.companies.map(item => item.count),
                  backgroundColor: colors.secondary,
                  borderColor: colors.secondary,
                  borderWidth: 1,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
  },
  title: {
    fontSize: '2rem',
    color: colors.primary,
    marginBottom: '2rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
  },
  statCard: {
    backgroundColor: colors.white,
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    textAlign: 'center' as const,
  },
  statTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1rem',
    color: colors.textLight,
    fontWeight: '500',
  },
  statNumber: {
    margin: 0,
    fontSize: '2.5rem',
    color: colors.primary,
    fontWeight: 'bold',
  },
  chartsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '2rem',
    marginTop: '2rem',
  },
  chartCard: {
    backgroundColor: colors.white,
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  chartTitle: {
    margin: '0 0 1rem 0',
    fontSize: '1.2rem',
    color: colors.primary,
    fontWeight: '600',
  },
};

export default Dashboard;